import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, Clock, MapPin, CheckCircle, XCircle, HelpCircle, Trophy } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { LeagueScheduledMatch } from '@/hooks/useLeagueMatches';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const formatPadelDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const date = new Date(y, m - 1, d);
  const formatted = format(date, "EEEE, d MMMM", { locale: es });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

interface WeekInfo {
  number: number;
  date: string;
  status: 'past' | 'current' | 'future';
  time?: string;
}

interface LeagueMatchCarouselProps {
  matches: LeagueScheduledMatch[];
  weeks: WeekInfo[];
  selectedWeek: number;
  onWeekChange: (weekNumber: number) => void;
  userAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  opponentAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  isLoading?: boolean;
  padelStatusLabels?: boolean;
  currentUserRegistrationId?: string | null;
}

const AttendanceBadge = ({ status, label }: { status: 'confirmed' | 'not_attending' | 'pending'; label: string }) => {
  const config = {
    confirmed: { bg: 'bg-green-50 text-green-700', icon: CheckCircle },
    not_attending: { bg: 'bg-red-50 text-red-700', icon: XCircle },
    pending: { bg: 'bg-amber-50 text-amber-700', icon: HelpCircle },
  };
  const { bg, icon: Icon } = config[status];
  return (
    <div className={`flex items-center text-xs font-medium rounded-full py-1 px-2.5 ${bg}`}>
      <Icon className="h-3 w-3 mr-1" />
      <span>{label}</span>
    </div>
  );
};

const LeagueMatchCarousel: React.FC<LeagueMatchCarouselProps> = ({
  matches,
  weeks,
  selectedWeek,
  onWeekChange,
  userAttendanceStatus,
  opponentAttendanceStatus,
  isLoading = false,
  padelStatusLabels = false,
  currentUserRegistrationId
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrentIndex(api.selectedScrollSnap());
    api.on('select', () => setCurrentIndex(api.selectedScrollSnap()));
  }, [api]);

  // Reset carousel when encounter changes
  useEffect(() => {
    if (api) api.scrollTo(0);
  }, [selectedWeek, api]);

  // Fetch results for matches that have a result_id
  const resultIds = useMemo(
    () => (matches || []).map(m => m.result_id).filter((x): x is string => !!x),
    [matches]
  );

  const { data: resultsData } = useQuery({
    queryKey: ['league-match-results-by-id', resultIds.sort().join(',')],
    queryFn: async () => {
      if (resultIds.length === 0) return {} as Record<string, any>;
      const { data, error } = await supabase
        .from('league_match_results')
        .select('id, score, winner_team, team1_games_won, team2_games_won')
        .in('id', resultIds);
      if (error) throw error;
      const map: Record<string, any> = {};
      (data || []).forEach((r: any) => { map[r.id] = r; });
      return map;
    },
    enabled: resultIds.length > 0,
  });

  if (isLoading) {
    return (
      <Card className="border border-border/50 rounded-xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando partidos...</span>
        </CardContent>
      </Card>
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <Card className="border border-border/50 rounded-xl">
        <CardContent className="p-6 text-center text-muted-foreground">
          No hay partidos programados para este encuentro
        </CardContent>
      </Card>
    );
  }

  const totalMatches = matches.length;

  return (
    <div className="space-y-3">
      {/* Attendance badges */}
      <div className="flex flex-wrap justify-center gap-2">
        <AttendanceBadge status={userAttendanceStatus} label={userAttendanceStatus === 'confirmed' ? 'Tu asistencia: Confirmada' : userAttendanceStatus === 'not_attending' ? 'Tu asistencia: No asistirá' : 'Tu asistencia: Pendiente'} />
        <AttendanceBadge status={opponentAttendanceStatus} label={opponentAttendanceStatus === 'confirmed' ? 'Rivales: Confirmado' : opponentAttendanceStatus === 'not_attending' ? 'Rivales: No asistirá' : 'Rivales: Pendiente'} />
      </div>

      {/* Swipeable carousel */}
      <Carousel setApi={setApi} className="w-full" opts={{ loop: false }}>
        <CarouselContent>
          {matches.map((match, idx) => {
            const team1Player1 = match.team1_player1_name;
            const team1Player2 = match.team1_player2_name;
            const team2Player1 = match.team2_player1_name;
            const team2Player2 = match.team2_player2_name;
            const team1 = team1Player2 ? `${team1Player1} / ${team1Player2}` : team1Player1;
            const team2 = team2Player2 ? `${team2Player1} / ${team2Player2}` : team2Player1;
            const matchDate = match.scheduled_date || '';
            const matchTime = match.scheduled_time ? match.scheduled_time.slice(0, 5) : '';

            const result = match.result_id ? resultsData?.[match.result_id] : null;
            const isFinished = !!result || match.status === 'completed';
            const winnerTeam: number | null = result?.winner_team ?? null;
            const score: string | null = result?.score ?? null;

            // Status header config: only "Por jugar" or "Finalizado"
            const statusBg = isFinished
              ? 'bg-muted text-muted-foreground'
              : 'bg-primary/10 text-primary';
            const StatusIcon = isFinished ? Trophy : Clock;
            const statusText = isFinished ? 'Finalizado' : (padelStatusLabels ? 'Programado' : 'Por jugar');

            return (
              <CarouselItem key={match.id || idx}>
                <Card className="border-primary/20 bg-primary/5 rounded-xl overflow-hidden">
                  {/* Status header */}
                  <div className={`flex items-center ${padelStatusLabels ? 'justify-center' : ''} gap-2 px-4 py-2 border-b border-primary/10 text-xs font-medium ${statusBg}`}>
                    <StatusIcon className="h-3.5 w-3.5" />
                    <span>{statusText}</span>
                  </div>

                  <CardContent className="p-4 space-y-3">
                    {/* Top row: index badge + win/lose badge */}
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {totalMatches > 1 && (
                        <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px] font-semibold">
                          Partido {idx + 1}/{totalMatches}
                        </Badge>
                      )}
                      {padelStatusLabels && isFinished && winnerTeam && currentUserRegistrationId && (() => {
                        const userTeam = match.team1_registration_id === currentUserRegistrationId
                          ? 1
                          : match.team2_registration_id === currentUserRegistrationId
                            ? 2
                            : null;
                        if (!userTeam) return null;
                        const won = userTeam === winnerTeam;
                        return (
                          <Badge
                            variant="secondary"
                            className={`text-[10px] font-semibold ${won ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                          >
                            {won ? 'Ganaste' : 'Perdiste'}
                          </Badge>
                        );
                      })()}
                    </div>

                    {/* Teams centered */}
                    <div className="space-y-1.5 text-center">
                      <div className={`text-sm truncate flex items-center justify-center gap-1.5 ${winnerTeam === 1 ? 'font-bold text-primary' : 'font-medium text-foreground'}`}>
                        {winnerTeam === 1 && <Trophy className="h-3.5 w-3.5 text-primary" />}
                        <span className="truncate">{team1}</span>
                      </div>
                      {isFinished && score ? (
                        <div className="text-sm font-bold text-foreground py-0.5">{score}</div>
                      ) : (
                        <div className="text-xs text-muted-foreground font-semibold">VS</div>
                      )}
                      <div className={`text-sm truncate flex items-center justify-center gap-1.5 ${winnerTeam === 2 ? 'font-bold text-primary' : 'font-medium text-foreground'}`}>
                        {winnerTeam === 2 && <Trophy className="h-3.5 w-3.5 text-primary" />}
                        <span className="truncate">{team2}</span>
                      </div>
                    </div>

                    {/* Footer: date · time · court */}
                    <div className="flex items-center justify-center gap-4 pt-3 border-t border-primary/10 text-xs text-muted-foreground">
                      {matchDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{padelStatusLabels ? formatPadelDate(matchDate) : matchDate}</span>
                        </div>
                      )}
                      {matchTime && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{matchTime}</span>
                        </div>
                      )}
                      {match.court_number && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>Cancha {match.court_number}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {/* Pagination dots */}
      {totalMatches > 1 && (
        <div className="flex justify-center gap-1.5">
          {matches.map((_, idx) => (
            <button
              key={idx}
              onClick={() => api?.scrollTo(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
              aria-label={`Ir al partido ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LeagueMatchCarousel;
